import { useState } from 'react';

import { WorkflowsArtifactHelm } from '@api/types.gen';

import { EnvironmentId } from '@/react/portainer/environments/types';
import { ResourcesTable } from '@/react/kubernetes/helm/release/ReleaseDetails/ResourcesTable/ResourcesTable';
import { useHelmRelease } from '@/react/kubernetes/helm/helmReleaseQueries/useHelmRelease';

import { Modal } from '@@/modals';
import { Button } from '@@/buttons';
import { CodeEditor } from '@@/CodeEditor';
import { Alert } from '@@/Alert';
import { NavTabs } from '@@/NavTabs';
import { TextTip } from '@@/Tip/TextTip';
import { Loading } from '@@/Widget';

type Tab = 'resources' | 'values';

/**
 * What a chart put on one environment.
 *
 * Read from the environment rather than from the deployment, because a chart is
 * rendered where it is installed: the source says what the chart is, and only
 * the cluster knows what came out of it. That is also why this is per
 * environment where a manifest deployment's resource list is not - two
 * environments running the same chart can hold different resources.
 */
export function HelmReleaseModal({
  environmentId,
  helm,
  name,
  onDismiss,
}: {
  environmentId: EnvironmentId;
  helm: WorkflowsArtifactHelm;
  name: string;
  onDismiss: () => void;
}) {
  const [tab, setTab] = useState<Tab>('resources');

  const releaseName = helm.releaseName ?? '';
  const namespace = helm.namespace ?? '';

  return (
    <Modal onDismiss={onDismiss} aria-label={`Release of ${name}`} size="xl">
      <Modal.Header title="Release details" />
      <Modal.Body>
        {!releaseName && (
          <Alert color="info">
            This chart has not been installed on this environment yet, so there
            is no release to read.
          </Alert>
        )}

        {!!releaseName && (
          <NavTabs<Tab>
            selectedId={tab}
            onSelect={setTab}
            // The same tabs the release page uses, so a release read from a
            // workflow looks like the same thing it is.
            type="pills"
            justified
            options={[
              {
                id: 'resources',
                label: 'Resources',
                children: (
                  <ResourcesTable
                    environmentId={environmentId}
                    name={releaseName}
                    namespace={namespace}
                    title={
                      <TextTip inline color="blue" className="!text-xs">
                        Only resources currently in the cluster will be
                        displayed.
                      </TextTip>
                    }
                  />
                ),
              },
              {
                id: 'values',
                label: 'Values',
                children: (
                  <ReleaseValues
                    environmentId={environmentId}
                    name={releaseName}
                    namespace={namespace}
                  />
                ),
              },
            ]}
          />
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onDismiss} color="secondary" data-cy="close-release">
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

/**
 * The values the release was installed with.
 *
 * The computed values rather than only what was supplied, so the chart's own
 * defaults are visible beside the overrides that were layered over them.
 */
function ReleaseValues({
  environmentId,
  name,
  namespace,
}: {
  environmentId: EnvironmentId;
  name: string;
  namespace: string;
}) {
  const { data, isLoading } = useHelmRelease(environmentId, name, namespace, {
    enabled: true,
  });

  if (isLoading) {
    return <Loading />;
  }

  return (
    <CodeEditor
      type="yaml"
      id="workflow-release-values"
      data-cy="workflow-release-values"
      value={data?.values?.computedValues ?? ''}
      readonly
      fileName={`Revision #${data?.version ?? 1}`}
      placeholder="No values found"
      height="60vh"
    />
  );
}
